import { Extension, type Editor } from "@tiptap/core"

type DoubleSpaceStorage = { openedViaDoubleSpace: boolean }

type MentionSuggestionAllow = (props: {
  editor: Editor
  state: Editor["state"]
  range: { from: number; to: number }
  isActive?: boolean
}) => boolean

/** Второй пробел открывает mentions, если это не IME и каталог разрешает меню. */
export function doubleSpaceShouldTrigger(prevChar: string, composing: boolean, suggestionAllowed: boolean): boolean {
  if (composing) return false
  if (!suggestionAllowed) return false
  return prevChar === " "
}

function getStorage(editor: Editor): DoubleSpaceStorage | undefined {
  return (editor.extensionStorage as unknown as Record<string, DoubleSpaceStorage | undefined>)[DoubleSpaceMention.name]
}

function mentionSuggestionAllowed(editor: Editor): boolean {
  const mention = editor.extensionManager.extensions.find((extension) => extension.name === "mention")
  const allow = mention?.options?.suggestion?.allow as MentionSuggestionAllow | undefined
  if (typeof allow !== "function") return true
  const { from } = editor.state.selection
  return allow({
    editor,
    state: editor.state,
    range: { from, to: from },
    isActive: false,
  })
}

export function wasOpenedViaDoubleSpace(editor: Editor): boolean {
  return getStorage(editor)?.openedViaDoubleSpace ?? false
}

export function clearDoubleSpaceTrigger(editor: Editor): void {
  const s = getStorage(editor)
  if (s) s.openedViaDoubleSpace = false
}

/**
 * При двух последовательных пробелах удаляет предыдущий и вставляет « @»,
 * запуская стандартный mention-попап.
 * Третий пробел (когда попап уже открыт) обрабатывается в suggestion onKeyDown —
 * он отменяет триггер и восстанавливает три пробела.
 */
export const DoubleSpaceMention = Extension.create({
  name: "doubleSpaceMention",

  addStorage() {
    return { openedViaDoubleSpace: false }
  },

  addKeyboardShortcuts() {
    return {
      Space: () => {
        const { from } = this.editor.state.selection
        if (from < 1) return false

        // Предыдущий символ — пробел, не чип и не другой узел.
        const prevChar = this.editor.state.doc.textBetween(from - 1, from)
        if (!doubleSpaceShouldTrigger(prevChar, this.editor.view.composing, mentionSuggestionAllowed(this.editor))) {
          return false
        }

        // Флаг устанавливаем до run() — suggestion onStart вызывается синхронно внутри run()
        this.storage.openedViaDoubleSpace = true
        this.editor
          .chain()
          .deleteRange({ from: from - 1, to: from })
          .insertContent(" @")
          .run()
        return true
      },
    }
  },
})
